/**
 * TarotCardPopup.tsx
 *
 * Full-screen modal overlay that displays a detailed tarot card reading for a
 * selected date.
 *
 * ## Features
 * - Entrance / exit fade + scale animation (200 ms).
 * - Scroll-driven image resize: the card image grows from 40 % to 90 % of the
 *   popup height as the user scrolls down.
 * - Displays card name, arcana type, keywords, reading text, themes, guidance,
 *   affirmation, and a list of all dates this card appears in the current year.
 * - Supports both **structured** readings (`{ upright, themes, guidance, affirmation }`)
 *   and **legacy plain-string** readings (rendered via `dangerouslySetInnerHTML`).
 * - Closes on: overlay click, × button, or Escape key.
 * - After 8 seconds, stores the card name in `sessionStorage` and dispatches a
 *   `tarot-card-name-updated` custom event so the header subtitle can be updated.
 * - Calls `onPopupClosedForTilt` immediately before executing any close path so
 *   the parent can arm the tilt-gesture detector.
 *
 * ## Props
 * @prop card                - The `TarotCard` to display.
 * @prop date                - The calendar date associated with this card.
 * @prop onClose             - Callback invoked after the exit animation completes.
 * @prop onPopupClosedForTilt - Optional callback invoked *before* the close animation
 *                             starts; used by `WeeklyCalendarClient` to arm tilt detection.
 */

'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '@/lib/themeContext';
import { getCardForDate, type TarotCard } from '@/data/tarotData';
import dayReadings from '@/data/day-readings.json';

/** Ordered month names used for date formatting within the popup. */
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** Ordered day names used for date formatting within the popup. */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Props accepted by `TarotCardPopup`. */
interface Props {
  /** The tarot card whose reading should be displayed. */
  card: TarotCard;
  /** The calendar date this card is assigned to. */
  date: Date;
  /** Called after the exit animation finishes — unmounts the popup. */
  onClose: () => void;
  /**
   * Called immediately before any close path executes (overlay click or × button).
   * Used by `WeeklyCalendarClient` to arm the tilt-gesture detector.
   */
  onPopupClosedForTilt?: () => void;
  /**
   * Current override map (dateStr → TarotCard) from WeeklyCalendarClient.
   * When provided, the "days with this card" list is computed using overrides
   * on top of the base session calendar so force-card placements are reflected.
   */
  overrides?: Record<string, TarotCard>;
  /**
   * When true, the daily personality traits & reading box is shown.
   * Set to true when the popup is opened from the calendar view.
   */
  fromCalendar?: boolean;
}

/**
 * Type guard that distinguishes a structured reading object from a plain string.
 *
 * @param reading - The value from `TarotCard.reading` to inspect.
 * @returns `true` if the reading has `upright`, `themes`, `guidance`, and `affirmation` fields.
 */
function isStructuredReading(reading: unknown): reading is { upright: string[]; themes: string[]; guidance: string; affirmation: string } {
  return typeof reading === 'object' && reading !== null && 'upright' in reading;
}

/**
 * Returns the zodiac sign name (lowercase) for a given month (1-based) and day.
 * Used to look up the correct PNG in /assets/images/zodiac/.
 */
function getZodiacSign(month: number, day: number): string {
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'aries';
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'taurus';
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'gemini';
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'cancer';
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'leo';
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'virgo';
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'libra';
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'scorpio';
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'sagittarius';
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'capricorn';
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'aquarius';
  return 'pisces'; // Feb 19 – Mar 20
}

export default function TarotCardPopup({ card, date, onClose, onPopupClosedForTilt, overrides, fromCalendar }: Props) {
  const { deckIndex } = useTheme();
  const [imageError, setImageError] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0); // 0–1
  const [visible, setVisible] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const displayedCardNameRef = useRef<string | null>(null);
  // Track whether the 8-second store timer has fired
  const cardStoredRef = useRef(false);

  // Entrance animation:
  // 1. Immediately clear any previously stored card / peek flag.
  // 2. After 8 seconds, store the current card name and set the peek flag.
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));

    // Clear immediately on open
    sessionStorage.removeItem('tarot-last-displayed-card-name');
    sessionStorage.removeItem('tarot-peek-enabled');
    cardStoredRef.current = false;

    const name = card.name.trim();
    displayedCardNameRef.current = name;

    // Store after 8 seconds
    const storeTimer = setTimeout(() => {
      if (name) {
        sessionStorage.setItem('tarot-last-displayed-card-name', name);
        sessionStorage.setItem('tarot-peek-enabled', 'true');
        window.dispatchEvent(new CustomEvent('tarot-card-name-updated', { detail: name }));
        cardStoredRef.current = true;
      }
    }, 8000);

    return () => clearTimeout(storeTimer);
  }, [card.name]);

  // Close with animation — request iOS motion permission only if a card was stored
  const handleClose = useCallback(async () => {
    // Only request iOS DeviceOrientation permission if the card was actually stored
    if (cardStoredRef.current) {
      type DOEWithPermission = typeof DeviceOrientationEvent & {
        requestPermission?: () => Promise<string>;
      };
      const DOE = DeviceOrientationEvent as DOEWithPermission;
      if (typeof DOE?.requestPermission === 'function') {
        try {
          await DOE.requestPermission();
        } catch {
          // Permission denied or error — parent will handle gracefully
        }
      }
    }

    onPopupClosedForTilt?.();
    setVisible(false);
    setTimeout(onClose, 200);
  }, [onClose, onPopupClosedForTilt]);

  // ESC key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleClose]);

  // Scroll tracking
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const progress = el.scrollTop / (el.scrollHeight - el.clientHeight);
    setScrollProgress(Math.min(1, Math.max(0, progress)));
  };

  // Tap or click anywhere on the overlay closes the popup.
  const handleOverlayClick = () => {
    handleClose();
  };

  // Image size interpolation based on scroll
  // At scroll=0: image is 40% height; at scroll=1: image is 90% of popup height
  const imageHeightPercent = 40 + scrollProgress * 50;

  const dateStr = `${DAY_NAMES[date.getDay()]}, ${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;

  // Build the list of days dynamically, respecting the current overrides map
  // so that any force-card placements are reflected immediately.
  const daysWithThisCard = (() => {
    const year = date.getFullYear();
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dates: string[] = [];

    for (let month = 0; month < 12; month++) {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const candidateDate = new Date(year, month, day);
        const candidateStr = candidateDate.toISOString().split('T')[0];
        // Prefer override entry, then fall back to session/base calendar
        const effectiveCard = overrides?.[candidateStr] ?? getCardForDate(candidateDate);
        if (effectiveCard?.id === card.id) {
          dates.push(`${day} ${shortMonthNames[month]}`);
        }
      }
    }

    return dates;
  })();

  const cardHeaderName = card.name.match(/^the\s+/i) ? card.name : `The ${card.name}`;

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 modal-backdrop
        transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleOverlayClick}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={`Tarot card reading for ${card.name}`}
    >
      <div
        className={`
          relative w-full max-w-[90vw] max-h-[90vh] bg-card rounded-2xl overflow-hidden
          border border-border card-glow-hover
          transition-all duration-300
          ${visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
          flex flex-col
        `}
        style={{ maxWidth: 'min(90vw, 680px)' }}
      >
        {/* Decorative top border */}
        <div className="h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-muted/80 hover:bg-muted
            border border-border hover:border-primary/50 flex items-center justify-center
            text-muted-foreground hover:text-primary transition-all duration-200
            focus:outline-none focus:ring-1 focus:ring-primary/50"
          aria-label="Close card reading"
        >
          <X size={14} />
        </button>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          className="overflow-y-auto scrollbar-arcane flex-1"
        >
          {/* ── TOP SECTION: Reading ── */}
          <div className="p-5 sm:p-6 lg:p-8">
            {/* Card identity header */}
            <div className="flex flex-col items-center gap-1 mb-5 text-center">
              <span className="text-primary/50 text-xs tracking-widest uppercase font-display">
                {date.getDate()} {MONTH_NAMES[date.getMonth()]}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-primary/40 text-sm" aria-hidden>✦</span>
                <h2 className="font-decorative text-xl sm:text-2xl text-primary tracking-wide">
                  {card.name}
                </h2>
                <span className="text-primary/40 text-sm" aria-hidden>✦</span>
              </div>
              <p className="font-body text-xs text-muted-foreground italic">
                {card.arcana === 'major' ? 'Major Arcana' : `${card.suit} — Minor Arcana`}
              </p>
              <p className="font-body text-[10px] text-muted-foreground/50 tracking-wider">
                {dateStr}
              </p>
            </div>

            <div className="arcane-divider mb-5" />

            {/* Keywords */}
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              {card.keywords.map(kw => (
                <span
                  key={`popup-kw-${card.id}-${kw}`}
                  className="font-body text-[10px] sm:text-xs text-primary/70 italic
                    px-2 py-0.5 rounded-full border border-primary/20 bg-primary/5"
                >
                  {kw}
                </span>
              ))}
            </div>


            {/* ── BOTTOM SECTION: Large Card Image ── */}
            <div className="flex flex-col items-center gap-3">
              <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase">
                ✦ The Card ✦
              </h3>

                {/* Card image */}
                <div className="flex justify-center mb-4">
                  <div className="w-32 rounded-xl overflow-hidden border border-border/60 shadow-lg"
                   style={{ maxHeight: '90vh', width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {!imageError && card.imageUrl[deckIndex] ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={card.imageUrl[deckIndex]}
                        alt={`Tarot card: ${card.name}`}
                        className="w-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="w-full h-44 flex items-center justify-center bg-muted/30 text-muted-foreground text-xs font-display">
                        {card.name}
                      </div>
                    )}
                  </div>
                </div>


              {/* Scroll hint */}
              {scrollProgress < 0.1 && (
                <p className="font-body text-[9px] text-muted-foreground/40 italic animate-pulse">
                  Scroll to reveal the full card
                </p>
              )}

              {/* Bottom padding */}
              <div className="h-8" />
            </div>

            {/* Main reading */}
            <div className="mb-5">
              <h3 className="font-display text-xs text-muted-foreground/60 tracking-widest uppercase mb-3 text-center">
                ✦ The Reading ✦
              </h3>
              {isStructuredReading(card.reading) ? (
                <p className="font-body text-sm sm:text-base text-foreground/85 leading-relaxed italic text-center">
                  {card.reading.upright[deckIndex]}
                </p>
              ) : (
                <div
                  className="font-body text-sm sm:text-base text-foreground/85 leading-relaxed italic text-center"
                  dangerouslySetInnerHTML={{ __html: card.reading as unknown as string }}
                />
              )}
            </div>

            {/* Themes — only shown for structured readings */}
            {isStructuredReading(card.reading) && (
              <div className="mb-5 bg-muted/30 rounded-xl p-4 border border-border/50">
                <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase mb-3 text-center">
                  Themes
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {card.reading.themes.map((theme, i) => (
                    <div
                      key={`popup-theme-${card.id}-${i}`}
                      className="flex items-start gap-1.5"
                    >
                      <span className="text-primary/60 text-xs mt-0.5 shrink-0" aria-hidden>◆</span>
                      <span className="font-body text-xs text-foreground/70 leading-snug">{theme}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Guidance — only shown for structured readings */}
            {isStructuredReading(card.reading) && (
              <div className="mb-5">
                <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase mb-2 text-center">
                  ✦ Guidance ✦
                </h3>
                <p className="font-body text-sm text-foreground/75 leading-relaxed text-center">
                  {card.reading.guidance}
                </p>
              </div>
            )}

            {/* Affirmation — only shown for structured readings */}
            {isStructuredReading(card.reading) && (
              <div className="bg-gradient-to-br from-primary/8 to-accent/8 rounded-xl p-4 border border-primary/20 mb-6">
                <h3 className="font-display text-[10px] text-primary/60 tracking-widest uppercase mb-2 text-center">
                  Today&apos;s Affirmation
                </h3>
                <p className="font-body text-sm text-primary/90 leading-relaxed italic text-center">
                  &ldquo;{card.reading.affirmation}&rdquo;
                </p>
              </div>
            )}

            {/* Daily personality traits & reading — only shown from calendar view */}
            {fromCalendar && (() => {
              const mm = String(date.getMonth() + 1).padStart(2, '0');
              const dd = String(date.getDate()).padStart(2, '0');
              const key = `${mm}${dd}` as keyof typeof dayReadings;
              const entry = dayReadings[key];
              if (!entry) return null;
              const [traits, reading] = entry as [string, string];
              const zodiacSign = getZodiacSign(date.getMonth() + 1, date.getDate());
              const zodiacLabel = zodiacSign.charAt(0).toUpperCase() + zodiacSign.slice(1);
              return (
                <div className="bg-muted/30 rounded-xl p-4 border border-border/50 mb-6">
                  <div className="flex items-start gap-4">
                    {/* Left column: zodiac image, top-aligned */}
                    <div className="shrink-0 pt-1">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={`/assets/images/zodiac/${zodiacSign}.png`}
                        alt={`${zodiacLabel} zodiac symbol`}
                        className="w-14 h-14 object-contain opacity-80"
                      />
                    </div>
                    {/* Right column: three centered rows */}
                    <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center">
                      {/* Row 1: title */}
                      <span className="font-display text-[10px] text-primary/50 tracking-widest uppercase">
                        ✦ Personality Traits ✦
                      </span>
                      {/* Row 2: personality traits */}
                      <p className="font-body text-xs text-primary/80 italic">
                        {traits}
                      </p>
                      {/* Row 3: daily reading */}
                      <p className="font-body text-sm text-foreground/75 leading-relaxed">
                        {reading}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Days with this card */}
            <div className="mb-5">
              <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase mb-2 text-center">
                {cardHeaderName} is on:
              </h3>
              <p className="font-body text-sm text-foreground/75 leading-relaxed text-center">
                {daysWithThisCard.join(', ')}
              </p>
            </div>
            {/* Bottom padding */}
          </div>
        </div>

        {/* Decorative bottom border */}
        <div className="h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent" />
      </div>
    </div>
  );
}